import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { POST } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

export default function CommentThread({ postId, onCountChange }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/posts/${postId}/comments`);
      setComments(data);
      onCountChange?.(data.length);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to comment");
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await api.post(`/posts/${postId}/comments`, { content: draft.trim() });
      setDraft("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Comment failed");
    } finally {
      setBusy(false);
    }
  };

  const vote = async (commentId, direction) => {
    if (!user) return toast.error("Sign in first");
    if (!user.is_member) return toast.error("Membership required to vote");
    try {
      const { data } = await api.post(`/comments/${commentId}/vote`, { direction });
      setComments((cs) =>
        cs.map((c) =>
          c.comment_id === commentId
            ? { ...c, votes_for: data.votes_for ?? c.votes_for, votes_against: data.votes_against ?? c.votes_against }
            : c
        )
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vote failed");
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
      {loading ? (
        <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">loading mentations…</p>
      ) : comments.length === 0 ? (
        <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">no comments yet</p>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => (
            <li
              key={c.comment_id}
              data-testid={POST.commentItem}
              className="bg-black/30 border border-white/5 rounded px-3 py-2"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-slate-200">{c.author_name}</p>
                <p className="text-[10px] font-mono text-slate-500">
                  {new Date(c.created_at).toLocaleString()}
                </p>
              </div>
              <p className="mt-1 text-sm text-slate-100 whitespace-pre-wrap">{c.content}</p>
              <div className="mt-2 flex gap-2">
                <button
                  data-testid={POST.commentVoteFor}
                  onClick={() => vote(c.comment_id, "for")}
                  className="telesis-pill bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 text-[11px]"
                >
                  For · {c.votes_for}
                </button>
                <button
                  data-testid={POST.commentVoteAgainst}
                  onClick={() => vote(c.comment_id, "against")}
                  className="telesis-pill bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 text-[11px]"
                >
                  Against · {c.votes_against}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {user ? (
        <form onSubmit={submit} className="flex gap-2 items-start">
          <textarea
            data-testid={POST.commentInput}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={1000}
            rows={2}
            placeholder="Add a sub-mentation…"
            className="flex-1 bg-[#12141c] border border-[#222634] rounded px-3 py-2 text-sm text-slate-100 resize-none"
          />
          <Button
            data-testid={POST.commentSubmit}
            type="submit"
            disabled={busy || !draft.trim()}
            size="sm"
            className="rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold"
          >
            Post
          </Button>
        </form>
      ) : (
        <p className="text-xs text-slate-500">
          <a href="/login" className="text-yellow-400 underline">Sign in</a> to comment or vote on sub-mentations.
        </p>
      )}
    </div>
  );
}
